import type { ReactNode } from "react";
import { FieldPoliciesPanel } from "../../modules/settings/FieldPoliciesPanel";
import { NedarimConnectionPanel } from "../../modules/settings/NedarimConnectionPanel";
import { EzcountConnectionPanel } from "../../modules/settings/EzcountConnectionPanel";
import { AiUsagePanel } from "../../modules/settings/AiUsagePanel";
import { AiCreditsPanel } from "../ai/credits/AiCreditsPanel";
import { AiFeedbackPanel } from "../ai/feedback/AiFeedbackPanel";
import { RecycleBinPanel } from "../../modules/settings/RecycleBinPanel";
import { DesignOptions } from "../design/DesignOptions";
import { YemotConnectionPanel } from "../sales/YemotConnectionPanel";
import "../../modules/settings/nedarimConnection.css";

/**
 * The settings hub (/settings?section=<key>) — one list for the whole organization.
 * Sections with `render` are edited in place (also right inside the "הגדרות קשורות" panel);
 * sections with `path` are links to the screen that owns the setting.
 */
export type HubSection = {
  key: string;
  group: string;
  icon: string;
  title: string;
  description: string;
  render?: () => ReactNode;
  path?: string;
  /** nav key the user must have for this section (permission-aware) */
  nav?: string;
  /** the user must have at least one of these permission keys (for sections without a screen of their own) */
  perm?: string[];
};

export const HUB_SECTIONS: HubSection[] = [
  {
    key: "design",
    group: "מראה",
    icon: "sun",
    title: "מראה ותצוגה",
    description: "מצב בהיר / כהה וסגנון — נשמר אצלך בלבד",
    render: () => <DesignOptions />,
  },
  {
    key: "field-policies",
    group: "ארגון והרשאות",
    icon: "eye",
    title: "מדיניות שדות",
    description: "אילו שדות כל תפקיד רואה, עורך או לא רואה בכלל",
    render: () => <FieldPoliciesPanel />,
  },
  {
    key: "permissions",
    group: "ארגון והרשאות",
    icon: "key",
    title: "הרשאות ותבניות תפקיד",
    description: "מה כל תפקיד רואה ועושה — לפי מסך ופעולה",
    path: "/settings/permissions",
  },
  {
    key: "users",
    group: "ארגון והרשאות",
    icon: "users",
    title: "משתמשים",
    description: "הזמנת משתמשים, תפקידים וסטטוס חברות",
    path: "/users",
    nav: "users",
  },
  {
    key: "guidance",
    group: "ארגון והרשאות",
    icon: "book",
    title: "מסמך הנחיות",
    description: "נהלי העבודה של הארגון",
    path: "/org-guidance",
    nav: "org_guidance",
  },
  {
    key: "nedarim",
    group: "חיבורים",
    icon: "repeat",
    title: "נדרים פלוס",
    description: "מפתח API, Webhooks, כתובות IP ושליפת עסקאות",
    render: () => <NedarimConnectionPanel />,
  },
  {
    key: "ezcount",
    group: "חיבורים",
    icon: "receipt",
    title: "EZcount",
    description: "הנפקת קבלות ומסמכי הנה״ח",
    render: () => <EzcountConnectionPanel />,
  },
  {
    key: "yemot",
    group: "חיבורים",
    icon: "phone",
    title: "ימות המשיח",
    description: "שליפת נתוני שמיעה מהקו: מי הקשיב לאיזו שלוחה ולכמה זמן — ישר ללידים",
    render: () => <YemotConnectionPanel />,
    perm: ["settings.integrations.yemot.manage"],
  },
  {
    key: "structure",
    group: "כספים",
    icon: "folder",
    title: "קטגוריות, קופות ותקציבים",
    description: "המבנה הכספי ומיפויי נדרים וקמפיינים",
    path: "/structure",
    nav: "finance_structure",
  },
  {
    key: "automations",
    group: "אוטומציות",
    icon: "sparkle",
    title: "אוטומציות",
    description: "מיילים, משימות ודוחות שקורים לבד",
    path: "/automations",
    nav: "automations",
  },
  {
    key: "ai-usage",
    group: "חיבורים",
    icon: "sparkle",
    title: "עוזר AI — צריכה ועלות",
    description: "כמה השתמשו בעוזר החודש, לפי משתמש, ומול התקציב",
    render: () => <AiUsagePanel />,
    perm: ["ai.usage.view"],
  },
  {
    key: "ai-credits",
    group: "חיבורים",
    icon: "wallet",
    title: "עוזר AI — קרדיטים והרשאות",
    description: "יתרת הקרדיטים, מי רשאי להשתמש ב-AI, מגבלה אישית ובקשת רכישה",
    render: () => <AiCreditsPanel />,
    perm: ["ai.credits.manage"],
  },
  {
    key: "ai-feedback",
    group: "חיבורים",
    icon: "heart",
    title: "עוזר AI — משובים",
    description: "👍 / 👎 שהמשתמשים נתנו לתשובות ה-AI, עם השאלה והתשובה",
    render: () => <AiFeedbackPanel />,
    perm: ["ai.usage.view"],
  },
  {
    key: "recycle-bin",
    group: "ארגון והרשאות",
    icon: "trash",
    title: "סל מחזור",
    description: "שחזור משימות, אוטומציות וטפסים שנמחקו ב־30 הימים האחרונים",
    render: () => <RecycleBinPanel />,
    perm: ["tasks.delete", "automations.manage", "forms.manage"],
  },
];

export function hubSection(key: string): HubSection | undefined {
  return HUB_SECTIONS.find((s) => s.key === key);
}
